import clsx from "clsx"
import { forwardRef } from "react"

import useDebouncedCallback from "@hooks/useDebouncedCallback"

import { faIcon } from "@utils/faIcon"

import { SkeletonTypography } from "@particles/index"

import { Icon } from "@atoms"

import styles from "./Button.module.scss"
import { ButtonProps } from "./Button.types"

const ICON_SIZE = 14

function Button(props: ButtonProps, ref: React.Ref<HTMLButtonElement>) {
    const {
        children,
        size,
        variant,
        type = "button",
        className,

        isTabbable = true,
        isFullWidth = false,

        leftIcon,
        leftIconClassName,
        rightIcon,
        rightIconClassName,

        onClick,
        noDebounce = false,

        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isProcessing = false,
        isDisabled = false,
        isLoading = false,

        ...rest
    } = props

    const debouncedClick = useDebouncedCallback(onClick)

    return (
        <button
            className={clsx(
                styles.base,
                className,
                {
                    [styles.isLoading]: isLoading,
                    [styles.isProcessing]: isProcessing,
                    [styles.isDisabled]: isDisabled,
                    [styles.isFocused]: _isFocused,
                    [styles.isHovered]: _isHovered,
                    [styles.isPressed]: isPressed,
                    [styles.fullWidth]: isFullWidth,
                },
                styles[`${variant}Variant`],
                styles[`${size}Size`],
            )}
            type={type}
            onClick={noDebounce ? onClick : debouncedClick}
            disabled={isDisabled || isProcessing || isLoading}
            aria-disabled={isDisabled}
            aria-busy={isProcessing || isLoading}
            tabIndex={isTabbable && !isDisabled ? 0 : -1}
            ref={ref}
            {...rest}
        >
            {isLoading ? (
                <span className={styles.labelSkeleton}>
                    <SkeletonTypography variant={size === "sm" ? "captionMD" : "bodyCompact"} />
                </span>
            ) : (
                <>
                    <span className={styles.content}>
                        {leftIcon && (
                            <Icon
                                container="fixedHeight"
                                icon={leftIcon}
                                className={leftIconClassName}
                                size={ICON_SIZE}
                            />
                        )}
                        {children && <span>{children}</span>}
                        {rightIcon && (
                            <Icon
                                container="fixedHeight"
                                icon={rightIcon}
                                className={rightIconClassName}
                                size={ICON_SIZE}
                            />
                        )}
                    </span>
                    {isProcessing && (
                        <span className={styles.spinner}>
                            <Icon
                                container="fixedHeight"
                                icon={faIcon({ name: "spinner-third", style: "regular" })}
                                spin={true}
                                size={ICON_SIZE}
                            />
                        </span>
                    )}
                </>
            )}
        </button>
    )
}

export default forwardRef(Button)
